import { useEffect, useMemo, useState } from "react";
import type { WalletSnapshot } from "../suite/types";
import {
  allowsCivitaiRed,
  fetchHandoffPicks,
  filterVideoPicks,
  type CivitaiRating,
  type HandoffKind,
  type VideoPick,
  type VideoProvider,
} from "../lib/videoEngines";

export type VideoHandoffParams = {
  prompt?: string;
  autogen?: string;
  provider?: string;
  pick?: string;
  model?: string;
  sku?: string;
  rating?: string;
};

type Props = {
  params: VideoHandoffParams;
  wallet: WalletSnapshot | null;
  onChange: (params: VideoHandoffParams) => void;
  purpose?: HandoffKind;
};

const PROVIDERS: { id: VideoProvider; label: string }[] = [
  { id: "any", label: "Any provider" },
  { id: "cloudflare", label: "Cloudflare" },
  { id: "hf", label: "Hugging Face" },
  { id: "civitai", label: "CivitAI" },
  { id: "suite", label: "Suite catalog" },
];

export function VideoHandoffFields({ params, wallet, onChange, purpose = "video" }: Props) {
  const [models, setModels] = useState<VideoPick[]>([]);
  const allowRed = allowsCivitaiRed(wallet);
  const provider = (params.provider as VideoProvider) || "any";
  const rating: CivitaiRating =
    params.rating === "red" && allowRed ? "red" : "green";
  const pick = params.pick === "id" ? "id" : "random";
  const noun = purpose === "image" ? "image" : "video";
  const poolHint = purpose === "image" ? "t2i" : "t2v";

  useEffect(() => {
    void fetchHandoffPicks(purpose).then(setModels).catch(() => setModels([]));
  }, [purpose]);

  const pool = useMemo(
    () => filterVideoPicks(models, { provider, rating, allowRed }),
    [models, provider, rating, allowRed],
  );

  const selectedSku = params.sku || params.model || "";
  const showCivitaiRating = provider === "civitai" || provider === "any";

  const patch = (next: Partial<VideoHandoffParams>) => {
    onChange({ ...params, ...next });
  };

  return (
    <>
      <select
        className="vf-select vf-binding-select"
        value={provider}
        onChange={(e) => {
          const p = e.target.value as VideoProvider;
          patch({
            provider: p,
            pick: "random",
            sku: "",
            model: "",
            rating: p === "civitai" ? rating : params.rating,
          });
        }}
        aria-label={`${noun} provider`}
      >
        {PROVIDERS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
      {showCivitaiRating && (
        <div className="vf-rating-row">
          <button
            type="button"
            className={`vf-rating vf-rating--green${rating === "green" ? " vf-rating--on" : ""}`}
            onClick={() => patch({ rating: "green", pick: "random", sku: "", model: "" })}
          >
            Green
          </button>
          <button
            type="button"
            className={`vf-rating vf-rating--red${rating === "red" ? " vf-rating--on" : ""}`}
            disabled={!allowRed}
            title={
              allowRed
                ? "CivitAI Red (18+)"
                : "Enable 18+ / CivitAI Red in AccessHub"
            }
            onClick={() => {
              if (!allowRed) return;
              patch({ rating: "red", pick: "random", sku: "", model: "" });
            }}
          >
            Red
          </button>
        </div>
      )}
      <select
        className="vf-select vf-binding-select"
        value={pick === "random" ? "random" : selectedSku}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "random") {
            patch({ pick: "random", sku: "", model: "" });
            return;
          }
          const m = pool.find((x) => x.skuId === v || x.modelId === v);
          patch({
            pick: "id",
            sku: m?.skuId || v,
            model: m?.modelId || v,
            provider: m?.provider || provider,
          });
        }}
        aria-label={`${noun} model`}
      >
        <option value="random">
          Random {provider === "any" ? "any provider" : PROVIDERS.find((p) => p.id === provider)?.label}
          {pool.length ? ` (${pool.length})` : ""}
        </option>
        {pool.map((m) => (
          <option key={`${m.provider}-${m.skuId}`} value={m.skuId}>
            {m.rating === "red" ? "● " : ""}
            {m.name}
          </option>
        ))}
      </select>
      <div className="vf-binding-hint">
        {pick === "random"
          ? `Each gesture picks a ${poolHint} engine from this pool.`
          : "This gesture always opens the named engine."}
        {!allowRed && showCivitaiRating ? " Red needs AccessHub 18+." : ""}
      </div>
    </>
  );
}
